import { Injectable } from "@angular/core";
import { TranslocoService } from "@jsverse/transloco";
import { EnumerationItem } from "projects/shared/models/enumeration-item.model";
import { Observable, of } from "rxjs";
import { map } from "rxjs/operators";
import { AssetType } from "../models/enums/asset-type-enum";
import { DataService } from "./data.service";
import { environment } from "../../environments/environment";

export interface CountryEnumerationItem {
  name: string;
  alpha2: string;
  alpha3: string;
  countryCode: string;
  iso_3166_2: string;
  region: string;
  subRegion: string;
  intermediateRegion: string;
  regionCode: string;
  subRegionCode: string;
  intermediateRegionCode: string;
}

@Injectable()
export class EnumsService {

  private _pricingEnums: any = undefined;

  private _allCountries: CountryEnumerationItem[] = undefined;

  private _countries = [
    {
      name: 'United States',
      value: 'us',
      areaCode: '+1'
    },
    {
      name: 'Spain',
      value: 'es',
      areaCode: '+34'
    },
    {
      name: 'Germany',
      value: 'de',
      areaCode: '+49'
    },
    {
      name: 'South Korea',
      value: 'kr',
      areaCode: '+82'
    },
    {
      name: 'France',
      value: 'fr',
      areaCode: '+33 '
    },
    {
      name: 'Brazil',
      value: 'br',
      areaCode: '+55'
    },
    {
      name: 'Russia',
      value: 'ru',
      areaCode: '+7'
    },
    {
      name: 'China',
      value: 'cn',
      areaCode: '+86'
    }
  ];

  private _turboInsuranceOccupancyTypes = [
    { value: 'Primary', name: "Primary" },
    { value: "SecondHome", name: "Second Home" },
    { value: "Investment", name: "Investment" },
    { value: "Other", name: "Other" },
  ]

  private _turboInsurancePropertyTypes = [
    { value: 'SingleFamily', name: "Single Family" },
    { value: "Townhouse", name: "Townhouse" },
    { value: "Condominium", name: "Condominium" },
    { value: "Cooperative", name: "Cooperative" },
    { value: "TwoToFourUnitProperty", name: "Two To Four Unit Property" },
    { value: "ManufacturedMobileHome", name: "Manufactured Mobile Home" }
  ]

  // SingleFamily = 1,
  // Townhouse,
  // Condominium,
  // Cooperative,
  // TwoToFourUnitProperty,
  // ManufacturedMobileHome

  private _militaryStatusTypes = [
    { value: "ActiveDuty", name: "Active Duty" },
    { value: "ReserveNationalGuardNeverActivated", name: "Reserve National Guard Never Activated" },
    { value: "Veteran", name: "Veteran" },
    { value: "Separated", name: "Separated" },
    { value: "Other", name: "Other" },
  ]

  private _ownershipShareTypes = [
    { value: "LessThan25Percent", name: "Less Than 25 Percent" },
    { value: "GreaterOrEqualTo25Percent", name: "Greater Than Or Equal To 25 Percent" },
    { value: "Other", name: "Other" },
  ]

  private _propertyTypes = [
    { value: "SFR", name: "SFR Detached" },
    { value: "SFRAttached", name: "SFR Attached" },
    { value: "PUD", name: "PUD" },
    { value: "Condominium", name: "Condominium" },
    { value: "Cooperative", name: "Cooperative" },
    { value: "DetachedCondominium", name: "Detached Condominium" },
    { value: "Townhouse", name: "2-4 Unit" },
    { value: "HighRiseCondominium", name: "High Rise Condominium" },
    { value: "MultiFamilyResidence", name: "Non Warranted Condominium" },
    { value: "ManufacturedHousing", name: "Manufactured Housing" },
    { value: "Modular", name: "Modular" },
    { value: "ManufacturedHousingSingleWide", name: "Manufactured Housing SingleWide " },
    { value: "ManufacturedHousingDoubleWide", name: "Manufactured Housing DoubleWide" },
    { value: "ManufacturedHousingMultiWide", name: "Manufactured Housing MultiWide" },
    { value: "ManufacturedHomeCondominium", name: "Manufactured Housing Condominium" },
    { value: "ManufacturedHomeCondominiumOrPUDOrCooperative", name: "Manufactured Housing Condominium,PUD,Co-op" },
    { value: "Detached", name: "Detached" },
    { value: "Timeshare", name: "Timeshare" },
    { value: "Other", name: "Other" }
  ]

  private _mortgageTypes = [
    { name: "Conventional", value: "Conventional" },
    { name: "FHA", value: "FHA" },
    { name: "HomeEquity", value: "HomeEquity" },
    { name: "NonConforming", value: "NonConforming" },
    { name: "NonAgency", value: "NonAgency" },
    { name: "Other", value: "Other" },
    { name: "USDA", value: "USDA" },
    { name: "VA", value: "VA" }
  ];

  private _maritalStatusTypes = [
    { name: "Married", value: "Married" },
    { name: "Separated", value: "Separated" },
    { name: "Single", value: "Single" },
    { name: "Other", value: "Other" }
  ];

  // WI 24451 — DomesticRelationshipTypeEnum values (must match the backend MortgageBorrower enum).
  private _domesticRelationshipTypes = [
    { name: "Civil Union", value: "CivilUnion" },
    { name: "Domestic Partnership", value: "DomesticPartnership" },
    { name: "Registered Reciprocal Beneficiary Relationship", value: "RegisteredReciprocalBeneficiaryRelationship" },
    { name: "Other", value: "Other" }
  ];

  private _downPaymentSourceTypes = [
    { name: "Cash On Hand", value: "CashOnHand" },
    { name: "Checkings/Savings", value: "CheckingSavings" },
    { name: "Equity From Pending Sale", value: "EquityOnPendingSale" },
    { name: "Equity From Subject Property", value: "EquityOnSubjectProperty" },
    { name: "Equity on Sold Property", value: "EquityOnSoldProperty" },
    { name: "Gift", value: "GiftFunds" },
    { name: "Other Funds Source", value: "Other" },
    { name: "Sale Of Assets", value: "SaleOfChattel" },
    { name: "Stocks, Bonds & Mutual Funds", value: "StocksAndBonds" }
  ];

  private _reoStatusTypes = [
    { name: "Sold", value: "Sold" },
    { name: "Pending Sale", value: "PendingSale" },
    { name: "Retain For Primary Or Secondary Residence", value: "RetainForPrimaryOrSecondaryResidence" },
    { name: "Rental Property", value: "RentalProperty" },
    { name: "Retained", value: "Retained" }
  ];

  private _usageTypes = [
    { name: "Primary Residence", value: "PrimaryResidence" },
    { name: "Secondary Residence", value: "SecondaryResidence" },
    { name: "Investment", value: "Investment" },
    { name: "Other", value: "Other" }
  ];

  private _militaryBranchOfServiceTypes = [
    { name: "Air Force", value: "AirForce" },
    { name: "Army", value: "Army" },
    { name: "Coast Guard", value: "CoastGuard" },
    { name: "Marines", value: "Marines" },
    { name: "Navy", value: "Navy" },
    { name: "Other", value: "Other" }
  ]

  private _businessTypes = [
    { name: "Sole Proprietor", value: "SoleProprietor" },
    { name: "LLC", value: "LLC" },
    { name: "S-Corp", value: "SCorp" },
    { name: "C-Corp", value: "CCorp" },
    { name: "Partnership", value: "Partnership" }
  ];

  private _giftOrGrantSources = [
    { name: "Community Non-Profit", value: "CommunityNonProfit" },
    { name: "Employer", value: "Employer" },
    { name: "Federal Agency", value: "FederalAgency" },
    { name: "Lender", value: "Lender" },
    { name: "Local Agency", value: "LocalAgency" },
    { name: "Relative", value: "Relative" },
    { name: "Religious Non-Profit", value: "ReligiousNonProfit" },
    { name: "State Agency", value: "StateAgency" },
    { name: "Unmarried Partner", value: "UnmarriedPartner" },
    { name: "Institutional", value: "Institutional" },
    { name: "Non-Parent Relative", value: "NonParentRelative" },
    { name: "Parent", value: "Parent" },
    { name: "Unrelated Friend", value: "UnrelatedFriend" },
    { name: "Other", value: "Other" }
  ];

  // URLA 2020 — "Type of Refinance" options bound to `mortgage.subjectProperty.refiPurpose`.
  // Values mirror `RefinancePurposeTypeEnum` in AdminV2 so the data round-trips cleanly
  // through the loan file and shows up in AdminV2's URLA editor with the same labels.
  private _refinancePurposes = [
    { name: "Change in Rate or Term", value: "ChangeInRateTerm" },
    { name: "No Cash-Out", value: "NoCashOutOther" },
    { name: "Cash-Out", value: "CashOutOther" },
    { name: "Cash-Out (Home Improvement)", value: "CashOutHomeImprovement" },
    { name: "Cash-Out (Debt Consolidation)", value: "CashOutDebtConsolidation" },
    { name: "Limited Cash-Out", value: "CashOutLimited" },
    { name: "Streamlined Refinance", value: "NoCashOutStreamlinedRefinance" },
    { name: "FHA Streamlined Refinance", value: "NoCashOutFHAStreamlinedRefinance" },
    { name: "Freddie Mac Owned Refinance", value: "NoCashOutFREOwnedRefinance" },
    { name: "Other", value: "Other" },
  ];

  // URLA 2020 — "Purpose of Refinance" options bound to `mortgage.subjectProperty.refinancePrimaryPurpose`.
  // Values mirror `RefinancePrimaryPurposeTypeEnum` in AdminV2.
  private _refinancePrimaryPurposes = [
    { name: "Debt Consolidation", value: "DebtConsolidation" },
    { name: "Home Improvement", value: "HomeImprovement" },
    { name: "Lower Interest Rate", value: "InterestRateReduction" },
    { name: "Shorter Loan Term", value: "MortgageTermReduction" },
    { name: "Cash", value: "Cash" },
    { name: "Asset Acquisition", value: "AssetAcquisition" },
    { name: "Equity Buyout", value: "EquityBuyout" },
    { name: "Education", value: "Education" },
    { name: "Other", value: "Other" },
  ];

  private _assetTypes: Array<{ name: string, value: AssetType }> = [
    { name: "Annuity", value: AssetType.Annuity },
    { name: "Automobile", value: AssetType.Automobile },
    { name: "Bond", value: AssetType.Bond },
    { name: "Bridge Loan Not Deposited", value: AssetType.BridgeLoanNotDeposited },
    { name: "Cash", value: AssetType.Cash },
    { name: "Certificate Of Deposit Time Deposit", value: AssetType.CertificateOfDepositTimeDeposit },
    { name: "Checking Account", value: AssetType.CheckingAccount },
    { name: "Earnest Money Cash Deposit Toward Purchase", value: AssetType.EarnestMoneyCashDepositTowardPurchase },
    { name: "Cash Gift", value: AssetType.CashGift },
    { name: "Gift of Equity", value: AssetType.GiftOfEquity },
    { name: "Individual Development Account", value: AssetType.IndividualDevelopmentAccount },
    { name: "Life Insurance", value: AssetType.LifeInsurance },
    { name: "Money Market Fund", value: AssetType.MoneyMarketFund },
    { name: "Mutual Fund", value: AssetType.MutualFund },
    { name: "Net Worth of Business Owned", value: AssetType.NetWorthOfBusinessOwned },
    { name: "Other Liquid Assets", value: AssetType.OtherLiquidAssets },
    { name: "Other Non Liquid Assets", value: AssetType.OtherNonLiquidAssets },
    { name: "Pending Net Sale Proceeds From Real Estate Assets", value: AssetType.PendingNetSaleProceedsFromRealEstateAssets },
    { name: "Proceeds From Sale Of Non Real Estate Asset", value: AssetType.ProceedsFromSaleOfNonRealEstateAsset },
    { name: "Relocation Money", value: AssetType.RelocationMoney },
    { name: "Retirement Fund", value: AssetType.RetirementFund },
    { name: "Savings Account", value: AssetType.SavingsAccount },
    { name: "Secured Borrowed Funds Not Deposited", value: AssetType.SecuredBorrowedFundsNotDeposited },
    { name: "Stock", value: AssetType.Stock },
    { name: "Stock Options", value: AssetType.StockOptions },
    { name: "Trust Account", value: AssetType.TrustAccount },
    { name: "Grant", value: AssetType.Grant }
  ];

  private _reoPropertyTypes = [
    { name: "Single Family", value: "SingleFamily" },
    { name: "Condo", value: "Condo" },
    { name: "Townhouse", value: "Townhouse" },
    { name: "Coop", value: "Coop" },
    { name: "Two To Four Unit Property", value: "TwoToFourUnitProperty" },
    { name: "Multi-Family / More Than Four Units", value: "MultifamilyMoreThanFourUnits" },
    { name: "Farm", value: "Farm" },
    { name: "Home & Business Combined", value: "HomeAndBusinessCombined" },
    { name: "Land", value: "Land" },
    { name: "Manufactured Mobile Home", value: "ManufacturedMobileHome" },
  ]

  private _propertyWillBeTypes = [
    { name: "Primary Residence", value: "PrimaryResidence", iconName: "stockholm-home-heart" },
    { name: "Second Home", value: "SecondaryResidence", iconName: "stockholm-sun" },
    { name: "Investment", value: "Investment", iconName: "stockholm-bank" }
  ]

  private _propertyTypesForDeclarations = [
    { name: "Primary Residence", value: "PrimaryResidence" },
    { name: "Second Home", value: "SecondHome" },
    { name: "Investment", value: "Investment" },
    { name: "FHA Secondary Residence", value: "FHASecondaryResidence" }
  ]

  private _heldTitleHows = [
    { name: "Sole", value: "Sole" },
    { name: "Joint With Spouse", value: "JointWithSpouse" },
    { name: "Joint With Other", value: "JointWithOtherThanSpouse" }
  ]

  private _bankruptcyTypes = [
    { name: "Chapter 7", value: "Chapter7" },
    { name: "Chapter 11", value: "Chapter11" },
    { name: "Chapter 12", value: "Chapter12" },
    { name: "Chapter 13", value: "Chapter13" }
  ]

  private _fhaSourceTypes = [
    { name: "Community Non-Profit", value: "CommunityNonProfit" },
    { name: "Employer", value: "Employer" },
    { name: "Federal Agency", value: "FederalAgency" }
  ]

  private _lienTypes = [
    { name: "First Lien", value: "FirstLien" },
    { name: "Second Lien", value: "SecondLien" },
    { name: "Third Lien", value: "ThirdLien" },
    { name: "Fourth Lien", value: "FourthLien" },
    { name: "Other", value: "Other" },
    { name: "HELOC", value: "HELOC" }
  ]

  // ['FirstLien', 'SecondLien', 'Other', 'HELOC', 'FourthLien', 'ThirdLien'],
  private _liabilityTypes = [
    { name: "Alimony", value: "Alimony" },
    { name: "Child Care", value: "ChildCare" },
    { name: "Child Support", value: "ChildSupport" },
    { name: "Collections Judgements And Liens", value: "CollectionsJudgementsAndLiens" },
    { name: "HELOC", value: "HELOC" },
    { name: "Installment", value: "Installment" },
    { name: "Job Related Expenses", value: "JobRelatedExpenses" },
    { name: "Lease Payments", value: "LeasePayments" },
    { name: "Mortgage Loan", value: "MortgageLoan" },
    { name: "Open 30Day Charge Account", value: "Open30DayChargeAccount" },
    { name: "Other Liability", value: "OtherLiability" },
    { name: "Revolving", value: "Revolving" },
    { name: "Separate Maintenance Expense", value: "SeparateMaintenanceExpense" },
    { name: "Other Expense", value: "OtherExpense" },
    { name: "Taxes", value: "Taxes" },
    { name: "Tax Lien", value: "TaxLien" },
    { name: "Borrower Estimated Total Monthly Liability Payment", value: "BorrowerEstimatedTotalMonthlyLiabilityPayment" },
    { name: "Deferred Student Loan", value: "DeferredStudentLoan" },
    { name: "Delinquent Taxes", value: "DelinquentTaxes" },
    { name: "Garnishments", value: "Garnishments" },
    { name: "Homeowners Association Lien", value: "HomeownersAssociationLien" },
    { name: "Monetary Judgment", value: "MonetaryJudgment" },
    { name: "Personal Loan", value: "PersonalLoan" },
    { name: "First Position Mortgage Lien", value: "FirstPositionMortgageLien" },
    { name: "Second Position Mortgage Lien", value: "SecondPositionMortgageLien" },
    { name: "Third Position Mortgage Lien", value: "ThirdPositionMortgageLien" },
    { name: "Unsecured Home Improvement Loan Installment", value: "UnsecuredHomeImprovementLoanInstallment" },
    { name: "Unsecured Home Improvement Loan Revolving", value: "UnsecuredHomeImprovementLoanRevolving" },
    { name: "Car Maintenance", value: "CarMaintenance" },
    { name: "Charitable Contributions", value: "CharitableContributions" },
    { name: "Clothing", value: "Clothing" },
    { name: "Dry Cleaning", value: "Dry Cleaning" },
    { name: "Entertainment", value: "Entertainment" },
    { name: "GroceryToiletry", value: "GroceryToiletry" },
    { name: "Health Insurance", value: "HealthInsurance" },
    { name: "Medical", value: "Medical" },
    { name: "Miscellaneous Living Expenses", value: "MiscellaneousLivingExpenses" },
    { name: "Net Rental Expense", value: "NetRentalExpense" },
    { name: "Payroll Insurance Deduction", value: "PayrollInsuranceDeduction" },
    { name: "Payroll Miscellaneous Deductions", value: "PayrollMiscellaneousDeductions" },
    { name: "Payroll Profit Sharing Deduction", value: "PayrollProfitSharingDeduction" },
    { name: "Payroll Retirement Deduction", value: "PayrollRetirementDeduction" },
    { name: "Payroll Tax Deduction", value: "PayrollTaxDeduction" },
    { name: "Union Dues", value: "UnionDues" }
  ]

  private _loanPurposeTypes = [
    { name: "Refinance", value: "Refinance" },
    { name: "Purchase", value: "Purchase" },
    { name: "Construction Only", value: "ConstructionOnly" },
    { name: "Construction To Perm", value: "ConstructionToPermanent" },
    { name: "Other", value: "Other" }
  ];

  private _companyStatuses = [
    new EnumerationItem('Active', 0),
    new EnumerationItem('Suspended', 1),
    new EnumerationItem('Declined', 2)
  ];

  private _languages: EnumerationItem[] = [
    { name: "English", value: "English" },
    { name: "Spanish", value: "Spanish" },
    { name: "Chinese", value: "Chinese" },
    { name: "Korean", value: "Korean" },
    { name: "Vietnamese", value: "Vietnamese" },
    { name: "Tagalog", value: "Tagalog" },
    { name: "Other", value: "Other" }
  ];

  private _singleChoiceOptionsProviders: EnumerationItem[] = [
    { name: "Loan Purpose", value: "LoanPurpose" },
    { name: "Loan Purpose V2", value: "LoanPurposeV2" },
    { name: "Occupancy", value: "Occupancy" },
    { name: "Default Property Type", value: "PropertyType" }
  ];

  private _borrowerCounselingFormatType: EnumerationItem[] = [
    { name: "Face to Face", value: "FaceToFace" },
    { name: "Internet", value: "Internet" },
    { name: "Telephone", value: "Telephone" },
    { name: "Other", value: "Other" }
  ];

  bindToNameAsValue: boolean = true;

  private _enumValues: any = {};

  private _mortgageEnums: any = undefined;

  public get reoStatusTypes() {
    return this.translateEnumArray(this._reoStatusTypes, 'reoStatus');
  }

  public get usageTypes() {
    return this.translateEnumArray(this._usageTypes, 'usage');
  }

  public get languages() {
    return this.translateEnumArray(this._languages as any, 'language');
  }

  public get militaryBranchOfServiceTypes() {
    return this.translateEnumArray(this._militaryBranchOfServiceTypes, 'militaryBranch');
  }

  public getCompanyStatuses = (): EnumerationItem[] => {
    return this._companyStatuses;
  }
  public get liabilityTypes() {
    return this.translateEnumArray(this._liabilityTypes, 'liabilityType');
  }

  public get lienTypes() {
    return this.translateEnumArray(this._lienTypes, 'lienType');
  }

  /** LLS-569: lien positions for the borrower-app lien modal — the Api LienPositionType without the obsolete HELOC value (use IsHeloc instead). */
  public get lienPositionTypes() {
    return this.lienTypes.filter(t => t.value !== 'HELOC');
  }

  public get fhaSourceTypes() {
    return this.translateEnumArray(this._fhaSourceTypes, 'giftOrGrantSource');
  }

  public get businessTypes() {
    return this.translateEnumArray(this._businessTypes, 'businessType');
  }

  public get assetTypes() {
    return this.translateEnumArray(this._assetTypes as any, 'assetType');
  }

  public get giftOrGrantSources() {
    return this.translateEnumArray(this._giftOrGrantSources, 'giftOrGrantSource');
  }

  public get bankruptyTypes() {
    return this.translateEnumArray(this._bankruptcyTypes, 'bankruptcyType');
  }

  public get mortgageTypes() {
    return this.translateEnumArray(this._mortgageTypes, 'mortgageType');
  }

  public get maritalStatusTypes() {
    return this.translateEnumArray(this._maritalStatusTypes, 'maritalStatus');
  }

  public get domesticRelationshipTypes() {
    return this.translateEnumArray(this._domesticRelationshipTypes, 'domesticRelationshipType');
  }

  public get downPaymentSourceTypes() {
    return this.translateEnumArray(this._downPaymentSourceTypes, 'downPaymentSource');
  }

  public get loanPurposeTypes() {
    return this.translateEnumArray(this._loanPurposeTypes, 'loanPurpose');
  }

  public maritalStatusName(value: string | undefined) {
    if (!value) {
      return "";
    }
    return this._transloco.translate(`enums.maritalStatus.${value}`);
  }

  public mortgageTypeName(value: any) {
    if (value !== null && value !== undefined) {
      return this._transloco.translate(`enums.mortgageType.${value}`);
    }
    return '';
  }

  /**
   * Loan purposes on an application are company-configured names (e.g. "New Client"), so only the standard
   * ones have an `enums.loanPurpose.*` key. Translate those; show any other name as entered instead of the raw key.
   */
  public loanPurposeName(value: any) {
    if (!value) {
      return '';
    }
    const key = `enums.loanPurpose.${value}`;
    const hasKey = this._lookupTranslation(this._transloco.getTranslation(this._transloco.getActiveLang()), key)
      ?? this._lookupTranslation(this._transloco.getTranslation('en'), key);
    return hasKey ? this._transloco.translate(key) : value;
  }

  public residencyStatusTypeName(value: string): string {
    if (value) {
      return this._transloco.translate(`enums.residencyStatus.${value}`);
    }
    return value;
  }

  public assetTypeName(value: AssetType): string {
    if (value) {
      return this._transloco.translate(`enums.assetType.${value}`);
    }
    return "";
  }

  public get reoPropertyTypes() {
    return this.translateEnumArray(this._reoPropertyTypes, 'reoPropertyType');
  }

  public get propertyTypes() {
    return this.translateEnumArray(this._propertyTypes, 'propertyType');
  }

  public get ownershipShareTypes() {
    return this.translateEnumArray(this._ownershipShareTypes, 'ownershipShare');
  }

  public get militaryStatusTypes() {
    return this.translateEnumArray(this._militaryStatusTypes, 'militaryStatus');
  }

  public get tueboInsuranceOccupancyTypes() {
    return this.translateEnumArray(this._turboInsuranceOccupancyTypes, 'turboInsuranceOccupancy');
  }

  public get tueboInsurancePropertyTypes() {
    return this.translateEnumArray(this._turboInsurancePropertyTypes, 'turboInsurancePropertyType');
  }

  public get propertyWillBeTypes() {
    return this._propertyWillBeTypes.map(item => ({
      ...item,
      name: this._transloco.translate(`enums.propertyWillBe.${item.value}`)
    }));
  }

  public get propertyTypesForDeclarations() {
    return this.translateEnumArray(this._propertyTypesForDeclarations, 'propertyTypeDeclarations');
  }

  public get heldTitleHows() {
    return this.translateEnumArray(this._heldTitleHows, 'heldTitleHow');
  }

  public get refinancePurposes() {
    return this.translateEnumArray(this._refinancePurposes, 'refinancePurpose');
  }

  public get refinancePrimaryPurposes() {
    return this.translateEnumArray(this._refinancePrimaryPurposes, 'refinancePrimaryPurpose');
  }

  public get singleChoiceOptionsProviders() {
    return this._singleChoiceOptionsProviders;
  }

  public propertyTypeName(value: any): string {
    if (value !== null && value !== undefined) {
      return this._transloco.translate(`enums.propertyType.${value}`);
    }
    return "";
  }

  public reoPropertyTypeName(value: any): string {
    if (value !== null && value !== undefined) {
      return this._transloco.translate(`enums.reoPropertyType.${value}`);
    }
    return "";
  }

  public propertyWillBeTypeName(value: any): string {
    if (value !== null && value !== undefined) {
      return this._transloco.translate(`enums.propertyWillBe.${value}`);
    }
    return "";
  }

  private _states = [
    { name: "AL", value: "AL" },
    { name: "AK", value: "AK" },
    { name: "AZ", value: "AZ" },
    { name: "AR", value: "AR" },
    { name: "CA", value: "CA" },
    { name: "CO", value: "CO" },
    { name: "CT", value: "CT" },
    { name: "DE", value: "DE" },
    { name: "DC", value: "DC" },
    { name: "FL", value: "FL" },
    { name: "GA", value: "GA" },
    { name: "HI", value: "HI" },
    { name: "ID", value: "ID" },
    { name: "IL", value: "IL" },
    { name: "IN", value: "IN" },
    { name: "IA", value: "IA" },
    { name: "KS", value: "KS" },
    { name: "KY", value: "KY" },
    { name: "LA", value: "LA" },
    { name: "ME", value: "ME" },
    { name: "MD", value: "MD" },
    { name: "MA", value: "MA" },
    { name: "MI", value: "MI" },
    { name: "MN", value: "MN" },
    { name: "MS", value: "MS" },
    { name: "MO", value: "MO" },
    { name: "MT", value: "MT" },
    { name: "NE", value: "NE" },
    { name: "NV", value: "NV" },
    { name: "NH", value: "NH" },
    { name: "NJ", value: "NJ" },
    { name: "NM", value: "NM" },
    { name: "NY", value: "NY" },
    { name: "NC", value: "NC" },
    { name: "ND", value: "ND" },
    { name: "OH", value: "OH" },
    { name: "OK", value: "OK" },
    { name: "OR", value: "OR" },
    { name: "PA", value: "PA" },
    { name: "RI", value: "RI" },
    { name: "SC", value: "SC" },
    { name: "SD", value: "SD" },
    { name: "TN", value: "TN" },
    { name: "TX", value: "TX" },
    { name: "UT", value: "UT" },
    { name: "VT", value: "VT" },
    { name: "VA", value: "VA" },
    { name: "WA", value: "WA" },
    { name: "WV", value: "WV" },
    { name: "WI", value: "WI" },
    { name: "WY", value: "WY" },
  ];

  private _stateNames = [
    { value: 'AL', name: 'Alabama' },
    { value: 'AK', name: 'Alaska' },
    { value: 'AZ', name: 'Arizona' },
    { value: 'AR', name: 'Arkansas' },
    { value: 'CA', name: 'California' },
    { value: 'CO', name: 'Colorado' },
    { value: 'CT', name: 'Connecticut' },
    { value: 'DE', name: 'Delaware' },
    { value: 'DC', name: 'District of Columbia' },
    { value: 'FL', name: 'Florida' },
    { value: 'GA', name: 'Georgia' },
    { value: 'HI', name: 'Hawaii' },
    { value: 'ID', name: 'Idaho' },
    { value: 'IL', name: 'Illinois' },
    { value: 'IN', name: 'Indiana' },
    { value: 'IA', name: 'Iowa' },
    { value: 'KS', name: 'Kansas' },
    { value: 'KY', name: 'Kentucky' },
    { value: 'LA', name: 'Louisiana' },
    { value: 'ME', name: 'Maine' },
    { value: 'MD', name: 'Maryland' },
    { value: 'MA', name: 'Massachusetts' },
    { value: 'MI', name: 'Michigan' },
    { value: 'MN', name: 'Minnesota' },
    { value: 'MS', name: 'Mississippi' },
    { value: 'MO', name: 'Missouri' },
    { value: 'MT', name: 'Montana' },
    { value: 'NE', name: 'Nebraska' },
    { value: 'NV', name: 'Nevada' },
    { value: 'NH', name: 'New Hampshire' },
    { value: 'NJ', name: 'New Jersey' },
    { value: 'NM', name: 'New Mexico' },
    { value: 'NY', name: 'New York' },
    { value: 'NC', name: 'North Carolina' },
    { value: 'ND', name: 'North Dakota' },
    { value: 'OH', name: 'Ohio' },
    { value: 'OK', name: 'Oklahoma' },
    { value: 'OR', name: 'Oregon' },
    { value: 'PA', name: 'Pennsylvania' },
    { value: 'RI', name: 'Rhode Island' },
    { value: 'SC', name: 'South Carolina' },
    { value: 'SD', name: 'South Dakota' },
    { value: 'TN', name: 'Tennessee' },
    { value: 'TX', name: 'Texas' },
    { value: 'UT', name: 'Utah' },
    { value: 'VT', name: 'Vermont' },
    { value: 'VA', name: 'Virginia' },
    { value: 'WA', name: 'Washington' },
    { value: 'WV', name: 'West Virginia' },
    { value: 'WI', name: 'Wisconsin' },
    { value: 'WY', name: 'Wyoming' },
  ]

  public get states() {
    return this._states;
  }

  public get stateNames() {
    return this._stateNames;
  }

  private _residencyBasisValues = [
    { name: "Own", value: "1" },
    { name: "Rent", value: "2" },
    { name: "LiveRentFree", value: "3" }
  ];

  public get residencyBasisValues() {
    return this._residencyBasisValues.map(item => ({
      ...item,
      name: this._transloco.translate(`enums.residencyBasis.${item.name}`)
    }));
  }

  public get countries() {
    return this._countries;
  }

  public residencyBasisName(value: string): string {
    const keyMap: Record<string, string> = {
      '0': 'Own',
      '1': 'Rent',
      '2': 'LivingRentFree',
      '3': 'Unknown'
    };
    const key = keyMap[value];
    if (key) {
      return this._transloco.translate(`enums.residencyBasis.${key}`);
    }
    return value;
  }

  public get borrowerCounselingFormatTypes() {
    return this.translateEnumArray(this._borrowerCounselingFormatType as any, 'borrowerCounselingFormat');
  }

  constructor(
    private readonly _dataService: DataService,
    private readonly _transloco: TranslocoService
  ) { }

  /**
   * Translates an enum-like array of `{ name, value }` items by looking up
   * `enums.${category}.${item.value}` in the active language's Transloco JSON.
   *
   * Graceful fallback: if the key is missing (Transloco returns the key itself
   * unchanged), keep the server-supplied English `name` so users see English
   * rather than a raw key. In development, logs a warning so missing keys
   * surface during testing.
   */
  public translateEnumArray(items: { name: string; value: string }[] | undefined, category: string): { name: string; value: string }[] {
    const lang = this._transloco.getActiveLang();
    const translations = this._transloco.getTranslation(lang) as any;
    // A list missing from the enumerations becomes empty rather than throwing, so callers that
    // translate inside a subscription still run the setup that follows the call.
    return (items ?? []).map(item => {
      const key = `enums.${category}.${item.value}`;
      const translated = this._lookupTranslation(translations, key);
      if (!translated) {
        if (!environment.production) {
          console.warn(`[EnumsService] Missing translation for '${key}' — falling back to server name '${item.name}'`);
        }
        return item;
      }
      return { ...item, name: translated };
    });
  }

  /**
   * Silently resolves a Transloco key against a loaded translations object,
   * handling either flat ("a.b.c": "v") or nested ({a: {b: {c: "v"}}}) storage.
   * Returns undefined if the key isn't present; never fires Transloco's
   * missing-handler.
   */
  private _lookupTranslation(translations: any, key: string): string | undefined {
    if (!translations) return undefined;
    if (typeof translations[key] === 'string') return translations[key];
    const parts = key.split('.');
    let obj: any = translations;
    for (const part of parts) {
      if (obj == null) return undefined;
      obj = obj[part];
    }
    return typeof obj === 'string' ? obj : undefined;
  }

  getMortgageEnumerations = (): Observable<any> => {
    if (this._mortgageEnums) {
      return of(this._mortgageEnums);
    }
    return this.getEnumerations('LE.Lodasoft.Common.Enums.Mortgage').pipe(
      map((result) => {
        this._mortgageEnums = result;
        return this._mortgageEnums;
      })
    );
  };

  getEnumValue = (name: string): string => {
    return this._enumValues[name];
  };

  private getEnumerations = (namespace: string): Observable<any> => {
    return this._dataService
      .post(`api/Lookup/enum/namespace/${namespace}?api_key=lookup`, {})
      .pipe(
        map((result) => {
          return this.getEnumerationResult(result);
        })
      );
  };

  private getEnumerationResult = (enumDefinitions: any) => {
    const enums: any = {};
    const allEnumValuesFlattened: any = {};
    for (const enumType in enumDefinitions) {
      const enumeration = enumDefinitions[enumType];
      const pieces = enumType.split(/[\s.]+/);
      const enumName = pieces[pieces.length - 1];
      enums[enumName] = [];
      for (let i = 0; i <= enumeration.length - 1; i++) {
        allEnumValuesFlattened[enumName + '.' + enumeration[i].name] =
          enumeration[i];
        let value = enumeration[i].value;
        if (this.bindToNameAsValue) {
          value = enumeration[i].name;
        }
        if (!enumeration[i].hidden) {
          const convertedEnumeration = new EnumerationItem(
            enumeration[i].displayName,
            value
          );
          convertedEnumeration.alternateValue = enumeration[i].name;
          enums[enumName].push(convertedEnumeration);
        }
        if (!Object.prototype.hasOwnProperty.call(this._enumValues, enumName + enumeration[i].name)) {
          this._enumValues[enumName + enumeration[i].name] = value;
        }
      }
    }
    return enums;
  };

  getPricingEnumerations = (): Observable<any> => {
    if (this._pricingEnums) {
      return of(this._pricingEnums);
    }
    return this.getEnumerations('LE.Lodasoft.Common.Models.Pricing').pipe(
      map((result) => {
        this._pricingEnums = result;
        return this._pricingEnums;
      })
    );
  };

  getAllCountries = (): Observable<CountryEnumerationItem[]> => {
    if (this._allCountries) {
      return of(this._allCountries);
    }

    // countries has been populated from address "https://github.com/lukes/ISO-3166-Countries-with-Regional-Codes/blob/master/all/all.json"
    return this._dataService.getRelativePath('./assets/countries.json').pipe(
      map((result) => {
        this._allCountries = result;
        return this._allCountries;
      })
    );
  }
}
